/**
 * Full settings page: sidebar (identity + nav) + main (panels). Same structure as plugin/lectura
 * but built from a small section registry and panel content builders.
 */

import { createEl } from '../core/dom';
import type { AuthService, ChatRoleService } from '../core/types';
import type { PresentationManagementService } from '../services/presentation-firestore';
import {
  NAV_ICONS,
  applyHighlightColorToDOM,
  buildAppearancePanelContent,
  buildTTSPanelContent,
  buildShortcutsPanelContent,
  buildAccountPanelContent
} from './profile-settings-ui';
import { getSetting, HIGHLIGHT_COLORS } from '../settings';
import { buildPresentationPanelContent } from './presentation-settings-panel';

export const SETTINGS_SECTIONS = [
  { id: 'appearance', label: 'Appearance' },
  { id: 'tts', label: 'Text-to-Speech' },
  { id: 'presentation', label: 'Presentation' },
  { id: 'shortcuts', label: 'Shortcuts' },
  { id: 'account', label: 'Account' }
] as const;

export type SettingsSectionId = (typeof SETTINGS_SECTIONS)[number]['id'];
const SETTINGS_SECTION_ID_SET = new Set<string>(SETTINGS_SECTIONS.map((section) => section.id));

export function isSettingsSectionId(value: string | null | undefined): value is SettingsSectionId {
  if (!value) return false;
  return SETTINGS_SECTION_ID_SET.has(String(value).trim().toLowerCase());
}

function getSettingsSectionFromQuery(query: string | null | undefined): SettingsSectionId | undefined {
  if (!query) return undefined;
  const params = new URLSearchParams(query);
  const raw = String(params.get('settings') || '').trim().toLowerCase();
  return isSettingsSectionId(raw) ? raw : undefined;
}

export function getSettingsSectionFromLocation(input: string): SettingsSectionId | undefined {
  if (!input) return undefined;
  const fromDirectQuery = getSettingsSectionFromQuery(input);
  if (fromDirectQuery) return fromDirectQuery;

  try {
    const url = new URL(input, typeof window !== 'undefined' ? window.location.href : 'https://localhost');
    const fromSearch = getSettingsSectionFromQuery(url.search);
    if (fromSearch) return fromSearch;
    const hash = String(url.hash || '');
    const hashQueryIdx = hash.indexOf('?');
    if (hashQueryIdx >= 0) return getSettingsSectionFromQuery(hash.slice(hashQueryIdx));
    return undefined;
  } catch {
    const hashIdx = input.indexOf('#');
    if (hashIdx >= 0) {
      const hash = input.slice(hashIdx + 1);
      const hashQueryIdx = hash.indexOf('?');
      if (hashQueryIdx >= 0) return getSettingsSectionFromQuery(hash.slice(hashQueryIdx));
    }
    return undefined;
  }
}

export interface CreateSettingsPageOptions {
  authService: AuthService;
  roleService?: ChatRoleService;
  presentationService?: PresentationManagementService;
  getPresentationId?: () => string;
  getSlidesVersion?: () => string;
  defaultSection?: SettingsSectionId;
  /** Omit sidebar entries and panels (e.g. remote shell: hide TTS and keyboard shortcuts). */
  excludeSections?: ReadonlyArray<SettingsSectionId>;
  onClose: () => void;
}

export interface SettingsPageApi {
  getPage(): HTMLElement;
  show(section?: SettingsSectionId): void;
  hide(): void;
}

const SETTINGS_SECTION_META: Record<SettingsSectionId, { title: string; desc: string }> = {
  appearance: {
    title: 'Appearance',
    desc: "Choose your identity and highlight color for chat and voiceover context."
  },
  tts: {
    title: 'Text-to-Speech',
    desc: 'Configure language, voice, speed, and playback behavior.'
  },
  presentation: {
    title: 'Presentation',
    desc: 'Assign presenters, manage privacy, and review presenter access requests.'
  },
  shortcuts: {
    title: 'Shortcuts',
    desc: 'Quick keyboard controls for presenting and navigating efficiently.'
  },
  account: {
    title: 'Account',
    desc: 'Manage sign-in, linked providers, and personal data controls.'
  }
};

function getLecturaBrandLogoHref(): string {
  if (typeof document === 'undefined') return '/favicon.svg';
  const iconLink = document.querySelector('link[rel~="icon"]') as HTMLLinkElement | null;
  const rawHref = String(iconLink?.getAttribute('href') || iconLink?.href || '/favicon.svg').trim();
  if (!rawHref) return '/favicon.svg';
  try {
    if (rawHref.startsWith('data:')) return rawHref;
    const base = typeof window !== 'undefined' && window.location?.href ? window.location.href : 'https://localhost/';
    return new URL(rawHref, base).toString();
  } catch {
    return '/favicon.svg';
  }
}

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

export function createSettingsPage(opts: CreateSettingsPageOptions): SettingsPageApi {
  const { authService, roleService, presentationService, getPresentationId, getSlidesVersion, onClose } = opts;
  const excludeSet = new Set<SettingsSectionId>(
    (opts.excludeSections || []).filter((id): id is SettingsSectionId => isSettingsSectionId(id))
  );
  const visibleSections = SETTINGS_SECTIONS.filter((s) => !excludeSet.has(s.id));
  const visibleIds = new Set<SettingsSectionId>(visibleSections.map((s) => s.id));

  let defaultSection: SettingsSectionId =
    opts.defaultSection && visibleIds.has(opts.defaultSection) ? opts.defaultSection : 'appearance';
  if (!visibleIds.has(defaultSection)) {
    defaultSection = visibleSections[0]?.id ?? 'appearance';
  }

  const panels: Record<string, HTMLElement> = {};
  let activeSection: SettingsSectionId = defaultSection;

  function resolveVisibleSection(id?: SettingsSectionId): SettingsSectionId {
    if (id && visibleIds.has(id)) return id;
    return defaultSection;
  }

  const NAV_BASE =
    'w-full flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.02] px-3 py-3 text-sm font-medium text-white/80 transition hover:bg-white/[0.05] hover:text-white';
  const NAV_ACTIVE =
    'border-[color-mix(in_oklab,rgb(var(--notes-tts-highlight-rgb))_28%,transparent)] ' +
    'bg-[color-mix(in_oklab,rgb(var(--notes-tts-highlight-rgb))_12%,transparent)] ' +
    'text-white/90 ring-1 ring-[color-mix(in_oklab,rgb(var(--notes-tts-highlight-rgb))_18%,transparent)]';
  const MOBILE_NAV_ACTIVE = 'text-white/90';
  const ICON_ACCENT = 'text-[rgb(var(--notes-tts-highlight-rgb))]';

  function ensureHighlightVar(): void {
    const id = String(getSetting('appearance', 'highlightColorId') || '').trim() || 'purple';
    const rgb = HIGHLIGHT_COLORS.find((c) => c.id === id)?.rgb ?? HIGHLIGHT_COLORS[0]?.rgb ?? '124,58,237';
    applyHighlightColorToDOM(rgb);
  }
  ensureHighlightVar();

  function syncSettingsQueryParam(section?: SettingsSectionId): void {
    if (typeof window === 'undefined' || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    if (section) {
      url.searchParams.set('settings', section);
    } else {
      url.searchParams.delete('settings');
    }
    const next = url.pathname + url.search + url.hash;
    window.history.replaceState(window.history.state, '', next);
  }

  const page = createEl('div', {
    className:
      'rf-profile-page rf-settings-page fixed inset-0 z-[10002] p-4 lg:p-6 max-[980px]:p-0 max-[980px]:flex max-[980px]:flex-col max-[980px]:justify-end ' +
      'backdrop-blur-xl ' +
      'opacity-0 invisible pointer-events-none transition-opacity duration-200',
    role: 'dialog',
    'aria-label': 'Settings',
    'aria-modal': 'true'
  });
  const shell = createEl('div', {
    className:
      'mx-auto h-[min(92vh,940px)] max-[980px]:h-[min(98vh,940px)] max-[640px]:h-[min(99dvh,99vh)] w-[min(1280px,100%)] overflow-hidden rounded-3xl max-[980px]:rounded-none max-[640px]:rounded-t-2xl border border-white/10 max-[980px]:border-0 ' +
      'max-[980px]:border-t max-[980px]:border-[color-mix(in_oklab,var(--color-white)_10%,transparent)] ' +
      'bg-slate-950/70 shadow-[0_30px_90px_rgba(0,0,0,0.55)] max-[980px]:shadow-[0_-20px_56px_rgba(0,0,0,0.58)] flex flex-col'
  });
  const topbar = createEl('header', {
    className: 'flex items-center justify-between gap-4 px-4 py-3 sm:px-6 border-b border-white/10 bg-white/[0.03]'
  });
  const titleRow = createEl('div', { className: 'flex items-center gap-3 min-w-0' });
  const brand = createEl('img', {
    className: 'h-7 w-7',
    src: getLecturaBrandLogoHref(),
    alt: '',
    'aria-hidden': 'true'
  });
  const title = createEl('h1', { className: 'text-sm font-semibold tracking-tight text-white/90 truncate' });
  title.textContent = 'Settings';
  titleRow.appendChild(brand);
  titleRow.appendChild(title);
  topbar.appendChild(titleRow);
  const closeBtn = createEl('button', {
    type: 'button',
    className:
      'h-10 w-10 rounded-xl border border-white/10 bg-white/5 text-white/80 grid place-items-center text-2xl ' +
      'leading-none transition hover:bg-white/10 hover:text-white active:translate-y-px',
    'aria-label': 'Close settings'
  });
  closeBtn.textContent = '×';
  topbar.appendChild(closeBtn);
  shell.appendChild(topbar);

  const body = createEl('div', { className: 'flex-1 min-h-0 flex flex-col lg:flex-row' });
  const sidebar = createEl('aside', {
    className:
      'hidden lg:flex lg:w-[320px] min-h-0 overflow-y-auto border-r border-white/10 bg-white/[0.02] ' +
      'p-4 sm:p-6 flex-col gap-4',
    'aria-label': 'Settings sections'
  });

  function renderIdent(el: HTMLElement): void {
    const u = authService.getCurrentUser?.() ?? null;
    const guest = !u || !!u.isAnonymous;
    const role = roleService?.getRole?.() ?? 'viewer';
    const badgeClass =
      'inline-flex items-center rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-[0.14em] ';
    const roleBadge =
      role === 'admin'
        ? '<span class="' + badgeClass + ' border-amber-400/20 bg-amber-400/10 text-amber-100">Admin</span>'
        : role === 'presenter'
          ? '<span class="' + badgeClass + ' border-amber-400/20 bg-amber-400/10 text-amber-100">Presenter</span>'
          : '<span class="' + badgeClass + ' text-white/70">Viewer</span>';
    if (guest) {
      el.innerHTML =
        '<div class="h-11 w-11 rounded-full bg-amber-400/15 text-amber-100 grid place-items-center font-semibold">?</div>' +
        '<div class="min-w-0 flex flex-col gap-1">' +
        '<span class="text-sm font-semibold text-white/95 truncate">Guest</span>' +
        '<span class="' +
        badgeClass +
        ' text-white/70">Local only</span>' +
        '</div>';
    } else {
      const name = u.displayName || u.email || 'Signed in';
      const email = u.email && u.email !== name ? u.email : '';
      const initial = name.charAt(0).toUpperCase();
      const photoUrl = String((u as { photoURL?: string }).photoURL || '').trim();
      const avatarMarkup = photoUrl
        ? '<img class="h-full w-full block object-cover rounded-full" src="' + escapeHtml(photoUrl) + '" alt="" referrerpolicy="no-referrer" />'
        : escapeHtml(initial);
      el.innerHTML =
        '<div class="h-11 w-11 rounded-full bg-amber-400/15 text-amber-100 grid place-items-center font-semibold overflow-hidden">' +
        avatarMarkup +
        '</div>' +
        '<div class="min-w-0 flex flex-col gap-1">' +
        '<span class="text-sm font-semibold text-white/95 truncate">' +
        escapeHtml(name) +
        '</span>' +
        (email ? '<span class="text-xs text-white/55 truncate">' + escapeHtml(email) + '</span>' : '') +
        '<span class="flex flex-wrap gap-2">' +
        roleBadge +
        '</span>' +
        '</div>';
    }
  }

  const ident = createEl('div', { className: 'flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4' });
  renderIdent(ident);
  sidebar.appendChild(ident);

  const navWrap = createEl('div', { className: 'flex flex-col gap-2' });
  const navTitle = createEl('p', { className: 'px-1 text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-white/45' });
  navTitle.textContent = 'Sections';
  navWrap.appendChild(navTitle);
  const navEl = createEl('nav', { className: 'flex flex-col gap-2', role: 'tablist', 'aria-label': 'Settings sections' });

  function appendSectionButtons(target: HTMLElement, opts?: { mobile?: boolean }): void {
    const mobile = !!opts?.mobile;
    visibleSections.forEach((s) => {
      const btn = createEl('button', {
        type: 'button',
        className:
          (mobile
            ? 'rf-mobile-nav-btn appearance-none bg-transparent border-0 shadow-none outline-none ' +
              'flex flex-col items-center justify-center gap-1 rounded-xl px-1 py-1.5 text-[0.65rem] font-semibold leading-none ' +
              'text-white/55 transition active:translate-y-px hover:text-white'
            : NAV_BASE) + (s.id === defaultSection ? ' rf-active ' + (mobile ? '' : NAV_ACTIVE) : '')
      });
      btn.setAttribute('data-section', s.id);
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', s.id === defaultSection ? 'true' : 'false');
      if (mobile) btn.setAttribute('aria-label', s.label);
      btn.innerHTML =
        '<span data-icon class="' +
        (mobile
          ? 'relative grid place-items-center h-8 w-8 rounded-xl bg-transparent text-white/70 ' +
            '[&>svg]:h-6 [&>svg]:w-6 [&>svg]:block [&>svg]:stroke-current [&>svg]:fill-none [&>svg]:[stroke-width:2]'
          : 'h-5 w-5 text-white/65') +
        '">' +
        (NAV_ICONS[s.id] || '') +
        (mobile
          ? '<span data-indicator class="absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-[rgb(var(--notes-tts-highlight-rgb))] opacity-0 transition-opacity"></span>'
          : '') +
        '</span><span class="' +
        (mobile ? 'truncate hidden min-[590px]:block' : 'truncate') +
        '">' +
        s.label +
        '</span>';
      target.appendChild(btn);
    });
  }

  appendSectionButtons(navEl);
  navWrap.appendChild(navEl);
  sidebar.appendChild(navWrap);
  body.appendChild(sidebar);

  /* ── Main ── */
  const main = createEl('main', { className: 'flex-1 min-h-0 overflow-y-auto flex flex-col gap-4' });

  function makePanel(id: SettingsSectionId): HTMLElement {
    const meta = SETTINGS_SECTION_META[id];
    const panel = createEl('section', {
      className:
        'p-5 sm:p-6 shadow-[0_20px_60px_rgba(0,0,0,0.35)]' +
        (id === defaultSection ? '' : ' hidden')
    });
    panel.setAttribute('role', 'tabpanel');
    panel.setAttribute('aria-label', meta.title);
    panel.setAttribute('data-panel', id);
    const hdr = createEl('div', { className: 'mb-5' });
    hdr.innerHTML =
      '<p class="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-white/45">' +
      escapeHtml(SETTINGS_SECTIONS.find((s) => s.id === id)?.label || '') +
      '</p>' +
      '<h2 class="mt-2 text-xl font-semibold tracking-tight text-white">' +
      escapeHtml(meta.title) +
      '</h2>' +
      '<p class="mt-2 text-sm leading-6 text-white/65 max-w-prose">' +
      escapeHtml(meta.desc) +
      '</p>';
    panel.appendChild(hdr);
    panels[id] = panel;
    main.appendChild(panel);
    return panel;
  }

  if (visibleIds.has('appearance')) {
    const appPanel = makePanel('appearance');
    const appContent = createEl('div', { className: 'rf-settings-appearance-content' });
    appPanel.appendChild(appContent);
    buildAppearancePanelContent(appContent);
  }

  if (visibleIds.has('tts')) {
    const ttsPanel = makePanel('tts');
    const ttsContent = createEl('div', { className: 'rf-settings-tts-content' });
    ttsPanel.appendChild(ttsContent);
    buildTTSPanelContent(ttsContent);
  }

  if (visibleIds.has('presentation')) {
    const presentationPanel = makePanel('presentation');
    const presentationContent = createEl('div', { className: '' });
    presentationPanel.appendChild(presentationContent);
    if (presentationService) {
      buildPresentationPanelContent(presentationContent, {
        authService,
        roleService,
        presentationService,
        getPresentationId
      });
    } else {
      presentationContent.innerHTML =
        '<p class="text-sm leading-6 text-white/60">Presentation management is not available in this build.</p>';
    }
  }

  if (visibleIds.has('shortcuts')) {
    const shortcutsPanel = makePanel('shortcuts');
    buildShortcutsPanelContent(shortcutsPanel);
  }

  const accPanel = makePanel('account');
  const accountIdent = createEl('div', {
    className: 'lg:hidden flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-4 mb-4'
  });
  renderIdent(accountIdent);
  const accContent = createEl('div', { className: '' });
  accPanel.appendChild(accountIdent);
  accPanel.appendChild(accContent);

  function refreshAccountPanel(): void {
    const u = authService.getCurrentUser?.() ?? null;
    renderIdent(accountIdent);
    buildAccountPanelContent(accContent, {
      user: u,
      authService,
      slidesVersion: getSlidesVersion?.() ?? '',
      onClose
    });
  }
  refreshAccountPanel();

  if (getSlidesVersion?.()) {
    const verEl = createEl('div', { className: 'mt-auto px-1 text-xs text-white/40' });
    verEl.textContent = 'Slides version: ' + getSlidesVersion();
    sidebar.appendChild(verEl);
  }

  body.appendChild(main);
  shell.appendChild(body);

  const mobileNavCols = Math.min(5, Math.max(1, visibleSections.length));
  const mobileNavGridClass =
    mobileNavCols <= 1
      ? 'grid-cols-1'
      : mobileNavCols === 2
        ? 'grid-cols-2'
        : mobileNavCols === 3
          ? 'grid-cols-3'
          : mobileNavCols === 4
            ? 'grid-cols-4'
            : 'grid-cols-5';
  const navMobile = createEl('nav', {
    className:
      'lg:hidden grid ' +
      mobileNavGridClass +
      ' gap-1 border-t border-white/10 bg-slate-950/70 px-2 py-2 backdrop-blur-xl ' +
      'shadow-[0_-12px_40px_rgba(0,0,0,0.35)]',
    role: 'tablist',
    'aria-label': 'Settings sections'
  });
  appendSectionButtons(navMobile, { mobile: true });
  shell.appendChild(navMobile);

  page.appendChild(shell);

  /* ── Events ── */
  closeBtn.addEventListener('click', onClose);
  page.addEventListener('click', (e) => {
    if (e.target === page) onClose();
  });
  const escHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  };

  function switchSection(id: SettingsSectionId): void {
    const navActiveTokens = NAV_ACTIVE.split(/\s+/).filter(Boolean);
    const mobileActiveTokens = MOBILE_NAV_ACTIVE.split(/\s+/).filter(Boolean);
    const iconAccentTokens = ICON_ACCENT.split(/\s+/).filter(Boolean);
    page.querySelectorAll('[data-section]').forEach((b) => {
      const isActive = b.getAttribute('data-section') === id;
      b.classList.toggle('rf-active', isActive);
      const isMobile = b.classList.contains('rf-mobile-nav-btn');
      if (isMobile) {
        if (isActive) b.classList.add(...mobileActiveTokens);
        else b.classList.remove(...mobileActiveTokens);
        const icon = (b as HTMLElement).querySelector('[data-icon]') as HTMLElement | null;
        if (icon) {
          if (isActive) icon.classList.add(...iconAccentTokens);
          else icon.classList.remove(...iconAccentTokens);
          icon.classList.toggle('text-white/70', !isActive);
          const dot = icon.querySelector('[data-indicator]') as HTMLElement | null;
          if (dot) dot.classList.toggle('opacity-100', isActive);
        }
      } else {
        if (isActive) b.classList.add(...navActiveTokens);
        else b.classList.remove(...navActiveTokens);
        const icon = (b as HTMLElement).querySelector('[data-icon]') as HTMLElement | null;
        if (icon) {
          if (isActive) icon.classList.add(...iconAccentTokens);
          else icon.classList.remove(...iconAccentTokens);
          icon.classList.toggle('text-white/65', !isActive);
        }
      }
      b.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    Object.keys(panels).forEach((k) => panels[k].classList.toggle('hidden', k !== id));
    activeSection = id;
    syncSettingsQueryParam(id);
  }

  page.querySelectorAll('[data-section]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-section') as SettingsSectionId;
      if (id) switchSection(id);
    });
  });

  function refreshRoleAndIdentity(): void {
    const renderBoth = () => {
      renderIdent(ident);
      renderIdent(accountIdent);
    };
    const u = authService.getCurrentUser?.() ?? null;
    if (!u || u.isAnonymous) {
      renderBoth();
      return;
    }
    if (!roleService) {
      renderBoth();
      return;
    }
    roleService.refresh().catch(() => {}).finally(renderBoth);
  }

  function show(section?: SettingsSectionId): void {
    renderIdent(ident);
    renderIdent(accountIdent);
    refreshRoleAndIdentity();
    refreshAccountPanel();
    const target = section !== undefined && section !== null ? section : activeSection || defaultSection;
    switchSection(resolveVisibleSection(target));
    document.addEventListener('keydown', escHandler);
    page.classList.add('rf-settings-page-open');
    page.classList.remove('opacity-0', 'invisible', 'pointer-events-none');
    page.classList.add('opacity-100', 'visible', 'pointer-events-auto');
  }

  function hide(): void {
    page.classList.remove('rf-settings-page-open');
    page.classList.add('opacity-0', 'invisible', 'pointer-events-none');
    page.classList.remove('opacity-100', 'visible', 'pointer-events-auto');
    document.removeEventListener('keydown', escHandler);
    syncSettingsQueryParam(undefined);
  }

  roleService?.subscribe?.(() => {
    renderIdent(ident);
    renderIdent(accountIdent);
  });
  authService.subscribeUserChange?.(() => {
    renderIdent(ident);
    renderIdent(accountIdent);
    refreshAccountPanel();
  });

  return {
    getPage: () => page,
    show,
    hide
  };
}
